// src/components/AboutDoctor.tsx
import React from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useTransform } from 'framer-motion';

const Section = styled.section`
    padding: 120px 0;
    position: relative;
    overflow: hidden;
`;

const AboutUsSection = styled(Section)`
    background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #C0C0C0 100%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(153, 1, 71, 0.3) 50%,
                transparent 100%
        );
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.03;
    background-image: radial-gradient(circle at 25% 25%, #990147 2px, transparent 0),
    radial-gradient(circle at 75% 75%, #990147 2px, transparent 0);
    background-size: 60px 60px;
`;

const AboutContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 3rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const AboutGrid = styled.div`
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 6rem;
    align-items: center;

    @media (max-width: 1200px) {
        gap: 4rem;
    }

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 4rem;
        text-align: center;
    }
`;

const AboutText = styled.div`
    position: relative;

    h3 {
        font-size: 3.5rem;
        font-weight: 800;
        margin-bottom: 2rem;
        background: linear-gradient(135deg, #333333 0%, #990147 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.1;
        position: relative;
        color: #333333; /* fallback color */

        @media (max-width: 1200px) {
            font-size: 3rem;
        }

        @media (max-width: 768px) {
            font-size: 2.5rem;
        }
    }
`;

const DecorativeLine = styled(motion.div)`
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, #990147, #990147);
    border-radius: 2px;
    margin-bottom: 3rem;

    @media (max-width: 968px) {
        margin-left: auto;
        margin-right: auto;
    }
`;

const AboutDescription = styled.div`
    p {
        font-size: 1.25rem;
        color: #555555;
        line-height: 1.8;
        margin-bottom: 2rem;
        font-weight: 400;
        text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const AboutHighlight = styled(motion.div)`
    background: linear-gradient(
            135deg,
            rgba(153, 1, 71, 0.95) 0%,
            rgba(153, 1, 71, 0.95) 100%
    );
    color: white;
    padding: 3rem;
    border-radius: 20px;
    margin: 3rem 0;
    box-shadow: 0 20px 40px rgba(153, 1, 71, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
                circle at 20% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
        ),
        radial-gradient(
                circle at 80% 20%,
                rgba(255, 255, 255, 0.05) 0%,
                transparent 50%
        );
        pointer-events: none;
    }

    p {
        font-size: 1.3rem;
        font-weight: 500;
        line-height: 1.7;
        margin: 0;
        font-style: italic;
        position: relative;
        z-index: 2;
        color: #FFFFFF;
        text-shadow: 0 2px 4px rgba(255, 255, 255, 0.1);
    }
`;

const QuoteIcon = styled.div`
    position: absolute;
    top: 1.5rem;
    left: 2rem;
    font-size: 3rem;
    opacity: 0.2;
    font-family: serif;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
    margin-top: 3rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const StatItem = styled(motion.div)`
    text-align: center;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 15px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);

    .number {
        font-size: 2.5rem;
        font-weight: 800;
        background: linear-gradient(135deg, #990147, #990147);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1;
        margin-bottom: 0.5rem;
    }

    .label {
        font-size: 0.9rem;
        color: #555555;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
`;

const ImageSection = styled.div`
    position: relative;
`;

const AboutImageWrapper = styled(motion.div)`
    position: relative;
    height: 500px;
    border-radius: 25px;
    overflow: hidden;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset;
`;

const AboutImage = styled(motion.img)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const FloatingElement = styled(motion.div)`
    position: absolute;
    background: linear-gradient(135deg, #990147, #990147);
    border-radius: 15px;
    box-shadow: 0 15px 35px rgba(153, 1, 71, 0.3);

    &.element-1 {
        width: 80px;
        height: 80px;
        top: -20px;
        right: -20px;
    }

    &.element-2 {
        width: 60px;
        height: 60px;
        bottom: 40px;
        left: -30px;
    }
`;

const ServiceBadge = styled(motion.div)`
    position: absolute;
    bottom: 2rem;
    left: 2rem;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    padding: 1rem 1.5rem;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.8);

    .service-title {
        font-weight: 700;
        color: #333333;
        margin-bottom: 0.25rem;
    }

    .service-desc {
        font-size: 0.875rem;
        color: #555555;
    }
`;

const AboutDoctor: React.FC = () => {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const rotateX = useTransform(mouseY, [-100, 100], [10, -10]);
    const rotateY = useTransform(mouseX, [-100, 100], [-10, 10]);

    const handleMouseMove = (event: React.MouseEvent) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        mouseX.set(x);
        mouseY.set(y);
    };

    const handleMouseLeave = () => {
        mouseX.set(0);
        mouseY.set(0);
    };

    const stats = [
        { number: '29+', label: 'Rokov skúseností' },
        { number: 'Tisíce+', label: 'Spokojných pacientov' },
        { number: '20+', label: 'Mediálnych vystúpení' },
    ];

    return (
        <AboutUsSection id="about">
            <BackgroundPattern />
            <AboutContent>
                <AboutGrid>
                    <AboutText>
                        <motion.h3
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            viewport={{ once: true, margin: '-100px' }}
                        >
                            MUDr. Soňa Belanská, psychiatrička
                        </motion.h3>

                        <DecorativeLine
                            initial={{ width: 0 }}
                            whileInView={{ width: 80 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            viewport={{ once: true }}
                        />

                        <AboutDescription>
                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                Štúdium na Jesseniovej lekárskej fakulte UK v Martine som ukončila v r. 1996 s vyznamenaním. Postupne som pracovala vo viacerých zariadeniach: Psychiatrická nemocnica Veľké Zálužie, Pinelova psychiatrická nemocnica Pezinok, Gerontopsychiatrické odd. PK SZU v Podunajských Biskupiciach, Psychiatrická ambulancia v Nemocnici Milosrdní bratia Bratislava.
                            </motion.p>

                            <AboutHighlight
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <QuoteIcon>"</QuoteIcon>
                                <p>
                                    Od roku 2024 som zapísaná v zozname znalcov, tlmočníkov a prekladateľov Ministerstva spravodlivosti SR ako znalkyňa pre odbor Zdravotníctvo a farmácia, odvetvie psychiatria.
                                </p>
                            </AboutHighlight>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                Okrem toho som absolvovala trénerský kurz a som certifikovaný tréner I. a II. kvalifikačného stupňa vo fitnes a kulturistike. Neskôr som na týchto kurzoch vyučovala predmety popisná anatómia a výživa. Ako expertku ste ma mohli vidieť vo vysielaní slovenských televíznych staníc (Supermama, Súdna sieň, Dámsky magazín) alebo počuť v diskusiách Slovenského rozhlasu (Pod pyramídou) či rádia Aktual.
                            </motion.p>

                            <StatsGrid>
                                {stats.map((stat, index) => (
                                    <StatItem
                                        key={stat.label}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.6,
                                            delay: 0.5 + index * 0.1,
                                        }}
                                        viewport={{ once: true }}
                                        whileHover={{ scale: 1.05, y: -5 }}
                                    >
                                        <div className="number">{stat.number}</div>
                                        <div className="label">{stat.label}</div>
                                    </StatItem>
                                ))}
                            </StatsGrid>
                        </AboutDescription>
                    </AboutText>

                    <ImageSection>
                        <AboutImageWrapper
                            initial={{ opacity: 0, x: 50, rotateY: 10 }}
                            whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
                            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
                            viewport={{ once: true }}
                            style={{
                                rotateX,
                                rotateY,
                                transformStyle: 'preserve-3d',
                            }}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                        >
                            <AboutImage
                                src="/sona-belanska.png"
                                alt="MUDr. Soňa Belanská"
                                initial={{ scale: 1.1 }}
                                whileInView={{ scale: 1 }}
                                transition={{ duration: 1.2, delay: 0.5 }}
                                viewport={{ once: true }}
                            />

                            <ServiceBadge
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.05 }}
                            >
                                <div className="service-title">
                                    Individualizovaný prístup k duševnému zdraviu
                                </div>
                                <div className="service-desc" />
                            </ServiceBadge>
                        </AboutImageWrapper>

                        <FloatingElement
                            className="element-1"
                            initial={{ opacity: 0, scale: 0, rotate: -45 }}
                            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ duration: 0.6, delay: 0.6, type: 'spring' }}
                            viewport={{ once: true }}
                            animate={{
                                y: [0, -20, 0],
                                rotate: [0, 5, 0],
                                transition: {
                                    duration: 4,
                                    ease: 'easeInOut',
                                    repeat: Infinity,
                                    type: 'tween',
                                },
                            }}
                        />

                        <FloatingElement
                            className="element-2"
                            initial={{ opacity: 0, scale: 0, rotate: 45 }}
                            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ duration: 0.6, delay: 0.7, type: 'spring' }}
                            viewport={{ once: true }}
                            animate={{
                                y: [0, 15, 0],
                                rotate: [0, -3, 0],
                                transition: {
                                    duration: 4,
                                    ease: 'easeInOut',
                                    repeat: Infinity,
                                    type: 'tween',
                                },
                            }}
                        />
                    </ImageSection>
                </AboutGrid>
            </AboutContent>
        </AboutUsSection>
    );
};

export default AboutDoctor;